/**
 * TERMS OF SERVICE PAGE
 * 
 * Standard terms of service for the platform
 * Should be reviewed by legal counsel before launch
 */

import { FileText } from 'lucide-react';
import { PageHeader } from '../components/common';

export default function TermsPage() {
  const lastUpdated = 'June 2026';

  return (
    <div className="min-h-screen py-12 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <PageHeader
          icon={FileText}
          title="Terms of Service"
          subtitle={`Last updated: ${lastUpdated}`}
          className="mb-6"
        />

        {/* Content */}
        <div className="glass-card p-8 space-y-8 font-body">
          <section>
            <h2 className="text-xl font-bold text-glass mb-3">1. Acceptance of Terms</h2>
            <p className="text-glass-secondary leading-relaxed">
              By accessing or using Polish ALL the Things ("the Service"), you agree to be bound by these Terms of Service. 
              If you do not agree to these terms, please do not use the Service.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-glass mb-3">2. Description of Service</h2>
            <p className="text-glass-secondary leading-relaxed">
              Polish ALL the Things is a nail art collection management platform that allows users to catalog their 
              nail polish and nail art supplies, share wishlists, buy/sell/trade items through our marketplace, 
              and connect with other nail art enthusiasts.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-glass mb-3">3. User Accounts</h2>
            <p className="text-glass-secondary leading-relaxed mb-3">
              To use certain features of the Service, you must create an account. You agree to:
            </p>
            <ul className="list-disc list-inside text-glass-secondary space-y-2 ml-4">
              <li>Provide accurate and complete information</li>
              <li>Maintain the security of your account credentials</li>
              <li>Notify us immediately of any unauthorized access</li>
              <li>Be responsible for all activities under your account</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-glass mb-3">4. User Content</h2>
            <p className="text-glass-secondary leading-relaxed mb-3">
              You retain ownership of content you upload to the Service. By uploading content, you grant us a
              non-exclusive, worldwide, royalty-free license to use, display, and distribute your content
              in connection with the Service.
            </p>
            <p className="text-glass-secondary leading-relaxed mb-3">
              <strong className="text-glass">Image rights.</strong> By uploading any image to the Service, you represent and warrant that
              (a) you took the image yourself, or (b) you own the copyright, or (c) you have received all
              necessary permissions from the copyright holder to share it publicly on this platform. You are
              solely responsible for verifying you have these rights before uploading. We may remove any
              image at any time if we receive a credible claim of infringement, and repeat infringers will
              have their accounts terminated.
            </p>
            <p className="text-glass-secondary leading-relaxed mb-3">
              <strong className="text-glass">DMCA / takedown.</strong> If you believe your work has been uploaded without
              authorization, please file a report via our{' '}
              <a href="/dmcaclaim" className="underline">DMCA claim form</a>. We respond to
              valid notices within 10 business days.
            </p>
            <p className="text-glass-secondary leading-relaxed">
              You are solely responsible for your content and agree not to upload content that is illegal,
              harmful, threatening, abusive, or otherwise objectionable. This includes but is not limited to
              sexually explicit material, hate speech, threats of violence, and content that impersonates
              another person or brand.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-glass mb-3">5. Brand Accounts</h2>
            <p className="text-glass-secondary leading-relaxed mb-3">
              Brand owners may claim their brand pages and upload official product information, including
              swatch imagery ("Brand Content"). By uploading Brand Content, you grant Polish ALL the Things
              users a limited, non-transferable, non-commercial license to:
            </p>
            <ul className="list-disc list-inside text-glass-secondary space-y-2 ml-4 mb-3">
              <li>Save your official swatch images to their personal collection as reference imagery for polishes they own</li>
              <li>Add your official swatch images to their personal wishlist as reference imagery for polishes they intend to acquire</li>
              <li>View, discuss, and reference your Brand Content within the Service</li>
            </ul>
            <p className="text-glass-secondary leading-relaxed mb-3">
              This license does not permit users to redistribute your images outside the Service, use them
              commercially, alter them, or claim them as their own work. Users cannot download and reupload
              your images as their own contributions.
            </p>
            <p className="text-glass-secondary leading-relaxed">
              You retain all copyright and other intellectual property rights in your Brand Content.
              You may request removal of any misuse by contacting us or filing a DMCA claim.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-glass mb-3">6. Marketplace Transactions</h2>
            <p className="text-glass-secondary leading-relaxed mb-3">
              The Service provides a platform for users to list items for sale or trade. We are not a party 
              to any transaction between users. Users are responsible for:
            </p>
            <ul className="list-disc list-inside text-glass-secondary space-y-2 ml-4">
              <li>Accurate descriptions of items</li>
              <li>Completing transactions as agreed</li>
              <li>Complying with all applicable laws</li>
              <li>Resolving disputes directly with other users</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-glass mb-3">7. Prohibited Conduct</h2>
            <p className="text-glass-secondary leading-relaxed mb-3">
              You agree not to:
            </p>
            <ul className="list-disc list-inside text-glass-secondary space-y-2 ml-4">
              <li>Violate any applicable laws or regulations</li>
              <li>Infringe on intellectual property rights</li>
              <li>Harass, abuse, or harm other users</li>
              <li>Spam or send unsolicited communications</li>
              <li>Attempt to gain unauthorized access to the Service</li>
              <li>Use the Service for any fraudulent purpose</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-glass mb-3">8. Intellectual Property</h2>
            <p className="text-glass-secondary leading-relaxed">
              The Service and its original content, features, and functionality are owned by Polish ALL the Things 
              and are protected by international copyright, trademark, and other intellectual property laws.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-glass mb-3">9. Disclaimer of Warranties</h2>
            <p className="text-glass-secondary leading-relaxed">
              THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED. 
              WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, SECURE, OR ERROR-FREE.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-glass mb-3">10. Limitation of Liability</h2>
            <p className="text-glass-secondary leading-relaxed">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, 
              SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING FROM YOUR USE OF THE SERVICE.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-glass mb-3">11. Termination</h2>
            <p className="text-glass-secondary leading-relaxed">
              We reserve the right to terminate or suspend your account at any time for any reason, 
              including violation of these Terms. You may delete your account at any time through Settings.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-glass mb-3">12. Changes to Terms</h2>
            <p className="text-glass-secondary leading-relaxed">
              We may modify these Terms at any time. We will notify users of significant changes. 
              Continued use of the Service after changes constitutes acceptance of the new Terms.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-glass mb-3">13. Contact Us</h2>
            <p className="text-glass-secondary leading-relaxed">
              If you have questions about these Terms, please contact us at{' '}
              <a href="mailto:hello@polishallthethings.com" className="text-glass hover:text-glass-secondary">
                hello@polishallthethings.com
              </a>
            </p>
          </section>
        </div>

        {/* Back to top */}
        <div className="text-center mt-8">
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="text-on-bg font-body"
          >
            ↑ Back to top
          </button>
        </div>
      </div>
    </div>
  );
}
